import 'dotenv/config';

/**
 * Sora API 客户端
 * 实现与 Sora 视频生成 API 的交互
 */

// 从环境变量读取配置
const SORA_API_KEY = process.env.SORA_API_KEY || '';
const SORA_BASE_URL = process.env.SORA_BASE_URL || '';
const SORA_POLL_INTERVAL = parseInt(process.env.SORA_POLL_INTERVAL || '5000', 10);
const SORA_MAX_POLL_TIME = parseInt(process.env.SORA_MAX_POLL_TIME || '500000', 10);

/**
 * 创建视频任务的参数
 */
export interface CreateVideoTaskParams {
  prompt: string;
  model: 'sora-2' | 'sora-2-pro';
  orientation: 'portrait' | 'landscape';
  size?: 'small' | 'large';
  duration: 10 | 15 | 25;
  images?: string[]; // 可选，用于图生视频模式
}

/**
 * 任务结果状态
 */
export interface TaskResult {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  videoUrl?: string;
  error?: string;
  progress?: number;
}

/**
 * API 错误类
 */
export class SoraApiError extends Error {
  constructor(message: string, public statusCode?: number) {
    super(message);
    this.name = 'SoraApiError';
  }
}

/**
 * 创建视频生成任务
 * @param params 任务参数
 * @returns 任务 ID
 */
export async function createVideoTask(params: CreateVideoTaskParams): Promise<string> {
  console.error('[SoraAPI] Creating video generation task...');
  console.error('[SoraAPI] Parameters:', JSON.stringify(params, null, 2));

  if (!SORA_API_KEY) {
    throw new SoraApiError('SORA_API_KEY environment variable is not set');
  }

  if (!SORA_BASE_URL) {
    throw new SoraApiError('SORA_BASE_URL environment variable is not set');
  }

  // 验证模型和时长的组合
  if (params.model === 'sora-2' && params.duration === 25) {
    throw new SoraApiError('sora-2 model only supports duration of 10 or 15 seconds');
  }

  const requestBody: any = {
    model: params.model,
    orientation: params.orientation,
    prompt: params.prompt,
    duration: params.duration,
  };

  // 如果提供了图片，则为图生视频模式
  if (params.images && params.images.length > 0) {
    requestBody.images = params.images;
  }

  try {
    const response = await fetch(`${SORA_BASE_URL}/v1/video/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${SORA_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new SoraApiError(
        `Failed to create video task: ${response.statusText}. ${errorText}`,
        response.status
      );
    }

    const result = await response.json() as any;

    // 打印完整响应用于调试
    console.error('[SoraAPI] Full API response:', JSON.stringify(result, null, 2));

    // API 返回格式：{ data: { id: "sora-2:task_xxx", ... }, message: "success", code: 200 }
    // 任务 ID 在 data.id 字段中
    const taskId = result.data?.id || result.id || result.taskId || result.task_id;

    if (!taskId) {
      console.error('[SoraAPI] ERROR: Cannot find task ID in response');
      console.error('[SoraAPI] Response structure:', Object.keys(result));
      throw new SoraApiError('API response does not contain task ID. Response: ' + JSON.stringify(result));
    }

    console.error(`[SoraAPI] Task created successfully. Task ID: ${taskId}`);
    return taskId;
  } catch (error) {
    if (error instanceof SoraApiError) {
      throw error;
    }
    throw new SoraApiError(`Network error: ${(error as Error).message}`);
  }
}

/**
 * 查询任务结果
 * @param taskId 任务 ID
 * @returns 任务结果
 */
export async function getTaskResult(taskId: string): Promise<TaskResult> {
  if (!SORA_API_KEY) {
    throw new SoraApiError('SORA_API_KEY environment variable is not set');
  }

  if (!SORA_BASE_URL) {
    throw new SoraApiError('SORA_BASE_URL environment variable is not set');
  }

  try {
    const response = await fetch(`${SORA_BASE_URL}/api/v3/predictions/${taskId}/result`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${SORA_API_KEY}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new SoraApiError(
        `Failed to get task result: ${response.statusText}. ${errorText}`,
        response.status
      );
    }

    const result = await response.json() as any;

    // 打印完整响应用于调试
    console.error('[SoraAPI] Task result response:', JSON.stringify(result, null, 2));

    // API 返回格式：
    // {
    //   data: {
    //     id: "sora-2:task_xxx",
    //     status: "completed",
    //     outputs: ["video_url"],
    //     error: null,
    //     ...
    //   },
    //   message: "success",
    //   code: 200
    // }
    const status = result.data?.status || result.status || 'pending';
    const videoUrl = result.data?.outputs?.[0] || result.data?.output || result.output || result.videoUrl || result.video_url;
    const error = result.data?.error || result.error;
    const progress = result.data?.progress || result.progress;

    return {
      status: mapApiStatus(status),
      videoUrl,
      error,
      progress,
    };
  } catch (error) {
    if (error instanceof SoraApiError) {
      throw error;
    }
    throw new SoraApiError(`Network error: ${(error as Error).message}`);
  }
}

/**
 * 映射 API 返回的状态到标准状态
 */
function mapApiStatus(apiStatus: string): TaskResult['status'] {
  const statusLower = apiStatus.toLowerCase();

  // 已完成状态
  if (statusLower === 'succeeded' || statusLower === 'completed' || statusLower === 'success' || statusLower === 'done') {
    return 'completed';
  }

  // 失败状态
  if (statusLower === 'failed' || statusLower === 'error' || statusLower === 'canceled' || statusLower === 'cancelled') {
    return 'failed';
  }

  // 处理中状态
  if (statusLower === 'processing' || statusLower === 'running' || statusLower === 'starting' ||
      statusLower === 'in_progress' || statusLower === 'in-progress' || statusLower === 'working') {
    return 'processing';
  }

  // 默认为等待状态
  return 'pending';
}

/**
 * 轮询直到任务完成或超时
 * @param taskId 任务 ID
 * @param pollInterval 轮询间隔（毫秒）
 * @param maxPollTime 最大轮询时间（毫秒）
 * @returns 视频 URL
 */
export async function pollTaskUntilComplete(
  taskId: string,
  pollInterval: number = SORA_POLL_INTERVAL,
  maxPollTime: number = SORA_MAX_POLL_TIME
): Promise<string> {
  console.error(`[SoraAPI] Starting to poll task ${taskId}...`);
  console.error(`[SoraAPI] Poll interval: ${pollInterval}ms, Max poll time: ${maxPollTime}ms`);

  const startTime = Date.now();
  let attempt = 0;

  while (true) {
    attempt++;
    const elapsedTime = Date.now() - startTime;

    if (elapsedTime >= maxPollTime) {
      throw new SoraApiError(
        `Task ${taskId} did not complete within ${maxPollTime}ms (${maxPollTime / 1000}s)`
      );
    }

    console.error(`[SoraAPI] Polling attempt ${attempt}, elapsed: ${Math.round(elapsedTime / 1000)}s`);

    const result = await getTaskResult(taskId);

    console.error(`[SoraAPI] Task status: ${result.status}${result.progress !== undefined ? `, progress: ${result.progress}%` : ''}`);

    if (result.status === 'completed') {
      if (!result.videoUrl) {
        console.error('[SoraAPI] ERROR: Task completed but no video URL found');
        console.error('[SoraAPI] Result object:', JSON.stringify(result, null, 2));
        throw new SoraApiError('Task completed but no video URL returned');
      }
      console.error(`[SoraAPI] Task completed successfully! Video URL: ${result.videoUrl}`);
      return result.videoUrl;
    }

    if (result.status === 'failed') {
      console.error(`[SoraAPI] Task failed. Error: ${result.error || 'Unknown error'}`);
      throw new SoraApiError(
        `Task ${taskId} failed${result.error ? `: ${result.error}` : ''}`
      );
    }

    // 等待下一次轮询
    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }
}
